import { DISPLAY_FORMATS, format, PARSE_FORMATS } from 'helper/dateTime';
import { OptionalProgramItemsEnum } from 'types/services/magnolia/enums/OptionalProgramItemsEnum';
import { RootStateType } from './programDays';
import { NormalizedProgramDayType, ProgramDayMinType, ProgramDayType } from './programDays';

/*
 * Returns the dayNumber of today
 */
export const getTodayDayNumber = (state: RootStateType): number | null => {
	const today = state.programDays.allIds
		.map((id: string) => {
			return (state.programDays.byId || {})[id];
		})
		.find((day: ProgramDayType) => day && day.today);

	return today && today.dayNumber ? today.dayNumber : null;
};

export const getTodayFromCalendar = (state: RootStateType): ProgramDayType | null => {
	const today = state.programDays.calendar.find((day: ProgramDayType) => day.today);
	return today || null;
};

export const getTodayDayNumberFromCalendar = (state: RootStateType): number | null => {
	const today: ProgramDayType | null = getTodayFromCalendar(state);
	return today && today.dayNumber ? today.dayNumber : null;
};

/*
 * Returns the dayNumber of today
 */
export const getIsToday = (state: RootStateType, dayNumber: number): boolean => {
	const todayDayNumber: number | null = getTodayDayNumber(state);
	return !!(todayDayNumber === dayNumber);
};

/*
 * Returns the calendar days
 */
export const getCalendar = (state: RootStateType): NormalizedProgramDayType[] => {
	return state.programDays.calendar.map((day: any) => {
		const optionalItemsList: OptionalProgramItemsEnum[] = [];

		if (day.optionalWorkout !== 'NONE') {
			optionalItemsList.push('ITOPTWORKOUT');
		}

		return {
			key: day.date.dayOfMonth,
			weekdayShort: format(day.date.date, DISPLAY_FORMATS.WEEKDAY_SHORT, PARSE_FORMATS.DATE) || '',
			weekday: format(day.date.date, DISPLAY_FORMATS.WEEKDAY, PARSE_FORMATS.DATE) || '',
			date: format(day.date.date, DISPLAY_FORMATS.DATE, PARSE_FORMATS.DATE) || '',
			isToday: day.today,
			isLastDayOfProgram: day.lastDayOfProgram,
			hasActiveProgram: day.hasActiveProgram,
			itemsCheckable: day.itemsCheckable,
			openItems: day.openItems,

			optionalItems: optionalItemsList,
			totalItems: day.totalItems,
			inFuture: day.inFuture,
			dayNumber: day.dayNumber,
		};
	});
};

/*
 * Returns the calendar today
 */
export const getCalendarToday = (state: RootStateType): ProgramDayMinType | undefined => {
	return state.programDays.calendarToday;
};

/*
 * Returns the calendar today
 */
export const getCurrentDayNumber = (state: RootStateType): number | undefined => {
	return state.programDays.currentDayNumber;
};

/*
 * Returns if the day is in the future
 */
export const getIsDayInFuture = (state: RootStateType, dayNumber: number | string | null): boolean => {
	const dayId = state.programDays.allIds.find((currentDayId: string) => {
		const dayById: any = (state.programDays.byId || {})[currentDayId];

		if (dayById) {
			return parseInt(dayById.dayNumber, 10) === parseInt(`${dayNumber}`, 10);
		}

		return false;
	});
	if (dayId) {
		return (state.programDays.byId || {})[dayId].inFuture;
	}
	return true;
};

/*
 * today and don't hasActiveProgram
 */
export const getIsTodayAfterProgramm = (state: RootStateType): boolean => {
	const calenderItemToday = state.programDays.calendar.find((item: ProgramDayType) => item.today);

	return Boolean(calenderItemToday && calenderItemToday.hasActiveProgram === false);
};

/*
 * Returns the program by by day number
 */
export const getDayByDayNumber = (state: RootStateType, dayNumber: number | string | null): ProgramDayType | null => {
	const dayId = state.programDays.allIds.find((currentDayId: any) => {
		const programDaysById = state.programDays.byId || {};

		if (programDaysById[currentDayId]) {
			return parseInt((programDaysById[currentDayId] as any).dayNumber, 10) === parseInt(dayNumber as any, 10);
		}

		return false;
	});

	if (dayId) {
		return (state.programDays.byId || {})[dayId];
	}

	return null;
};

/*
 * Returns if the calendar day has loaded
 * > Note: does not mean the days content has loaded too
 */
export const getHasDayLoaded = (state: RootStateType, dayNumber: number): boolean => {
	return getDayByDayNumber(state, dayNumber) !== null;
};

export const getIsDayCheckable = (state: RootStateType, day: ProgramDayType | null): boolean | null => {
	if (day !== null) {
		return day.itemsCheckable;
	}

	return null;
};

export const getOptionalItems = (state: RootStateType, dayNumber: number): OptionalProgramItemsEnum[] => {
	if (dayNumber) {
		const day = getDayByDayNumber(state, dayNumber);

		if (day) {
			return day.optionalItems;
		}
	}

	return [];
};

export const getStartDate = (state: RootStateType): string => {
	return state.programDays.calendar[0] ? state.programDays.calendar[0].date.date : '';
};

export const getLastDayOfProgram = (state: RootStateType): string => {
	const lastDay = state.programDays.calendar.filter((day: any) => day.lastDayOfProgram);
	return lastDay[0] ? lastDay[0].date.date : '';
};
